import { MouseEvent, useState, useMemo } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";

import { SizeAndScroll } from "@covertree/hooks";
import { MenuItemDef } from "@covertree/types";

interface TableActionMenuProps {
  rowDimensions: SizeAndScroll;
  tableDimensions: SizeAndScroll;
  actionMenuItems: MenuItemDef[];
  onRowActionClick?: () => void;
}

export const TableActionMenu = ({
  actionMenuItems,
  tableDimensions,
  rowDimensions,
  onRowActionClick,
}: TableActionMenuProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const distanceFromRightBorder = useMemo(
    () => rowDimensions.width - tableDimensions.width - tableDimensions.scroll,
    [rowDimensions.width, tableDimensions.width, tableDimensions.scroll]
  );

  const handleClick = (event: MouseEvent) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  return (
    <Box position="relative" top="50%" right={0}>
      <ClickAwayListener onClickAway={handleClose}>
        <Box
          id="action-button-wrapper"
          position="absolute"
          height={theme.spacing(5)}
          width={theme.spacing(5)}
          top="50%"
          right={distanceFromRightBorder > 0 ? distanceFromRightBorder : 0}
          mt={theme.spacing(1)}
          sx={{
            "&:before": {
              content: "''",
              display: "block",
              pointerEvents: "none",
              position: "absolute",
              left: theme.spacing(-2.5),
              top: 0,
              height: "100%",
              width: theme.spacing(5.5),
              background:
                "linear-gradient(270deg, #F9F7F1 53.65%, rgba(250, 248, 242, 0) 100%)",
            },
          }}>
          <IconButton
            aria-label="Options"
            sx={{
              backgroundColor: "action.hover",
              padding: theme.spacing(1),
              transform: `translateX(-${theme.spacing(1.5)})`,
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
            onClick={handleClick}
            size="large">
            <MoreVertIcon />
          </IconButton>
        </Box>
      </ClickAwayListener>
      <Menu
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}>
        {actionMenuItems.map(({ label, onClick }) => (
          <MenuItem
            key={label}
            onClick={() => {
              onClick();
              if (onRowActionClick) {
                onRowActionClick();
              }
            }}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
